// src/views/operateur/OperateurView.jsx — L'ÉCRAN DE L'OPÉRATEUR (lots 1 & 2,
// ADR-303 + ADR-304) : LE seul endroit qui envoie, et depuis le lot 2 la
// maison de sa mémoire, de ses habitudes et de son coupe-circuit.
//
// L'ÉTAT LOT 2 (ADR-304) — ce qui EXISTE ici, réellement branché :
//   · les cartes de décision (lot 1, inchangées) ;
//   · la bascule « Ce qu'il a fait » (OperateurFait : gestes + habitudes +
//     limite du jour) et « Retenu de vous » (OperateurMemoire) ;
//   · le COUPE-CIRCUIT : « Tout arrêter » / « Le relancer » (operateur_etat,
//     DB) — l'écran affiche l'état EFFECTIF (env ∧ base, leçon M5) ; à
//     l'arrêt, les gestes sont DÉSARMÉS (lecture seule sauf « Le relancer »).
//
//   · RÉVISION ASSUMÉE de Q1 (ADR-304 point 5) : le réglage « la prochaine
//     fois » EXISTE désormais — borné aux signalements d'habitude
//     (ACTES_FAIT_SEUL_ELIGIBLES, dans OperateurFait). Les cartes de réponse
//     voyageur, elles, GARDENT « il proposera toujours » (verdict V10) :
//     aucun envoi voyageur ne s'automatise par un segment.
//   · Q3 — la file ne porte QUE les réponses prêtes. Ni escaladee, ni echec
//     n'y fait de carte (la pastille ambre de l'Inbox reste la maison de
//     l'escalade).
//   · Q5 — plafond du jour atteint → l'écran LE DIT, du même count que le job.
//   · Micro — bouton HONNÊTE « pas encore » (D4).
//   · Le refus « toujours » a TROIS portées (exception / logement / partout,
//     leçon B2) et la réponse serveur DIT la maison (`maison`) : fiches
//     (flag mémoire éteint) ou mémoire (flag allumé) — l'écran adapte son
//     texte (leçon M6).
//
// LES QUATRE GESTES :
//   · Envoyer   → RÉCLAMER (serveur, rowcount + re-vérifs) → guichet EXISTANT
//     `inbox_reply` (iaDraft = le texte proposé — la capture de correction
//     existante joue si retouché) → CONFIRMER (contrat STRICT : seuls sent /
//     sent_dry_run transitionnent ; failed → la carte REVIENT avec l'erreur ;
//     sent_dry_run → « simulé — le maître d'envoi est fermé », jamais
//     « c'est parti »). AUCUN chemin d'envoi neuf, nulle part.
//   · Retoucher → édition puis même guichet avec iaDraft = brouillon d'origine.
//   · Non       → deux étapes du proto : « Pourquoi non ? » puis « Juste cette
//     fois, ou toujours ? » ; « toujours » (Q2) = la règle s'écrit dans la
//     fiche de CHAQUE logement éligible, APRÈS confirmation à l'écran, et
//     l'écran DIT dans quelles fiches elle est écrite.
//
// Le copilote actuel reste TEL QUEL. ⚠️ L'onglet IA de ParamsPanel, lui, a
// DISPARU (pivot) : sa prose vit désormais dans l'onglet « Réglages » ci-
// dessous — OperateurReglages.jsx. Il n'y avait aucun interrupteur à
// déménager : le lot 4 les avait déjà retirés.

import { useState, useEffect, useCallback, useRef } from 'react'
import '../../styles/fbz-admin.css'
import '../../styles/fbz-operateur.css'
import CarteGeste from './CarteGeste.jsx'
import LigneOrganes from './LigneOrganes.jsx'
import OperateurFait from './OperateurFait'
import OperateurMemoire from './OperateurMemoire'
import OperateurReglages from './OperateurReglages'
import VeilleCautions from './VeilleCautions'
import VeilleManques from './VeilleManques'
import { executerGeste } from '../../lib/geste-executer'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import { supabase } from '../../supabase'

async function postAction(body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Session expirée");
  const res = await fetch("/api/beds24-mapping", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(json?.message || json?.error || `Erreur ${res.status}`);
    e.code = json?.error;
    throw e;
  }
  return json;
}

const microIc = (t) => (
  <svg
    width={t}
    height={t}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <path d="M12 18v4" />
  </svg>
);

/** « 9h12 aujourd'hui » / « le 4 septembre » — quand la proposition est née. */
function quandCourt(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const jour = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
  }).format(d);
  const auj = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
  }).format(new Date());
  const [h, m] = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Paris",
  })
    .format(d)
    .split(":");
  const heure = m === "00" ? `${Number(h)}h` : `${Number(h)}h${m}`;
  if (jour === auj) return heure;
  const [, mo, da] = jour.split("-").map(Number);
  const mois = new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(2026, mo - 1, da)));
  return `le ${da === 1 ? "1er" : da} ${mois}`;
}

const initialesDe = (nom) =>
  String(nom || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0])
    .join("")
    .toUpperCase() || "?";

/* ═══ LA FEUILLE (par-dessus, comme à l'accueil) ═══════════════════ */
function Feuille({ titre, children, onFermer }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="operateur-feuille"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        background: "rgba(35,50,64,.42)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onFermer}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100vw)",
          maxHeight: "86vh",
          overflowY: "auto",
          background: "var(--fond)",
          borderRadius: "18px 18px 0 0",
          padding: "16px 16px 22px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: 14.5,
              fontWeight: 700,
              color: "var(--navy-d)",
            }}
          >
            {titre}
          </span>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              border: "none",
              background: "#e6eaef",
              color: "var(--gris-d)",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ChoixLigne({ titre, sous, onClic, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClic}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 11,
        width: "100%",
        textAlign: "left",
        background: "#fff",
        border: "1px solid var(--bord)",
        borderRadius: 12,
        padding: 12,
        minHeight: 56,
        marginBottom: 8,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <b style={{ display: "block", fontSize: 12.5, color: "var(--navy-d)" }}>
          {titre}
        </b>
        <span
          style={{
            display: "block",
            fontSize: 10.5,
            color: "var(--gris)",
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {sous}
        </span>
      </span>
      <span style={{ fontSize: 14, color: "var(--gris-d)", flexShrink: 0 }}>
        ›
      </span>
    </button>
  );
}

/* ═══ L'ÉCRAN ══════════════════════════════════════════════════════ */
// ⚠️ RÉSOLUTION 2026-09-08 : conflit NON additif. #1301 ajoutait l'onglet
// « reglages », main ajoutait la prop `onAller` (les gestes). Garder un seul
// côté aurait supprimé l'un des deux en silence.
export default function OperateurView({ cfg, onAller }) {
  const [source, setSource] = useState({ ok: null, data: null, motif: null })
  const [etats, setEtats] = useState({}) // id → { phase, erreur, simule, retouche, texte }
  const [refus, setRefus] = useState(null) // { prop, etape, motif, texte, ecrits, portee, maison }
  const [microNote, setMicroNote] = useState(null) // id de la carte qui a dit « pas encore »
  const [onglet, setOnglet] = useState('decisions') // decisions | fait | memoire | reglages
  const [feuilleArret, setFeuilleArret] = useState(false)
  const [arretBilan, setArretBilan] = useState(null) // { messages_arrives, passages_sautes }
  const [arretErreur, setArretErreur] = useState(null)
  const vivantRef = useRef(true)
  useEffect(() => { vivantRef.current = true; return () => { vivantRef.current = false } }, [])

  const nomLogement = useCallback(
    (id) => {
      for (const l of getAllLogsFlat(cfg || {}))
        if (l.id === id) return l.nom || id;
      return id || "—";
    },
    [cfg],
  );

  const charger = useCallback(async () => {
    try {
      const json = await postAction({ action: "operateur_propositions_list" });
      if (!vivantRef.current) return;
      setSource({ ok: true, data: json, motif: null });
    } catch (e) {
      console.error("[operateur] liste", e?.message || e);
      if (vivantRef.current)
        setSource({
          ok: false,
          data: null,
          motif: "La lecture des propositions a échoué.",
        });
    }
  }, []);
  useEffect(() => {
    const id = setTimeout(charger, 0);
    return () => clearTimeout(id);
  }, [charger]);

  const poserEtat = (id, patch) =>
    setEtats((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  // ── FAIRE UN GESTE : la MÊME séquence que l'envoi d'un brouillon —
  //     réclamer → guichet existant → confirmer — avec un autre guichet.
  //     L'appel part de CE navigateur, avec le jeton de Dimitri : l'opérateur
  //     ne détient jamais le moyen d'agir (cf. src/lib/geste-executer.js).
  const lancerGeste = useCallback(
    async (prop) => {
      const id = prop.id;
      poserEtat(id, { phase: "envoi", erreur: null });
      const r = await executerGeste({ proposition: prop, postAction });
      if (r.ok) {
        poserEtat(id, { phase: "fait", erreur: null });
        // La liste fait foi : on la recharge pour que la carte quitte la file.
        charger();
        return;
      }
      // Le message dit OÙ ça s'est arrêté — c'est la seule question qui compte
      // après un échec : est-ce que quelque chose est parti ?
      poserEtat(id, { phase: null, erreur: r.message });
      // Un refus du levier veut dire que l'état a changé : la liste le sait
      // mieux que la carte.
      if (r.etape === "levier_refuse" || r.etape === "reclamation") charger();
    },
    [charger],
  );

  // ── ENVOYER : réclamer → guichet existant → confirmer. ────────────────────
  const envoyer = useCallback(
    async (prop, texte, retouche) => {
      const id = prop.id;
      poserEtat(id, { phase: "envoi", erreur: null });
      try {
        // 1 · RÉCLAMER — le serveur re-vérifie (déjà répondu / le voyageur a
        //     ré-écrit) et pose en_envoi (rowcount=1, sinon stop net).
        await postAction({ action: "operateur_proposition_claim", id });
      } catch (e) {
        poserEtat(id, {
          phase: null,
          erreur: e?.message || "Réclamation refusée.",
        });
        charger(); // la carte a pu périmer — la liste fait foi
        return;
      }
      try {
        // 2 · ENVOYER — le guichet existant, inchangé. Les coordonnées se lisent
        //     AU CLIC (résa + expéditeur du message source), jamais pré-chargées.
        const { data: resaRows } = await supabase
          .from("reservations")
          .select("id, logement_id, beds24_booking_id, guest_email")
          .eq("id", prop.reservation_id)
          .limit(1);
        const resa = (resaRows && resaRows[0]) || null;
        const { data: srcRows } = await supabase
          .from("inbox_messages")
          .select("id, sender_email, subject")
          .eq("id", prop.source_message_id)
          .limit(1);
        const src = (srcRows && srcRows[0]) || null;
        const sujet =
          (src && src.subject) || (prop.meta && prop.meta.subject) || null;
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const res = await fetch("/api/send-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            type: "inbox_reply",
            data: {
              threadId: prop.thread_id,
              canaux: [prop.canal || "email"],
              body: texte,
              subject: sujet
                ? sujet.startsWith("Re:")
                  ? sujet
                  : `Re: ${sujet}`
                : null,
              reservationId: prop.reservation_id,
              logementId:
                prop.logement_id || (resa && resa.logement_id) || null,
              toEmail:
                (src && src.sender_email) || (resa && resa.guest_email) || null,
              beds24BookingId: resa ? resa.beds24_booking_id : null,
              inReplyToMessageId: prop.source_message_id,
              // Le brouillon D'ORIGINE part toujours : la capture de correction
              // EXISTANTE (ADR-249) joue quand le texte envoyé en diffère.
              iaDraft: prop.draft_texte,
              source: "operateur",
            },
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`);
        const results = Array.isArray(json.results) ? json.results : [];
        // 3 · CONFIRMER — le serveur lit les results[] et applique le contrat
        //     strict (sent / sent_dry_run seuls ; failed → retour proposee).
        const conf = await postAction({
          action: "operateur_proposition_confirm",
          id,
          results,
          retouche: retouche === true,
        });
        if (conf.statut === "proposee") {
          poserEtat(id, {
            phase: null,
            erreur: conf.erreur || "Rien n'est parti.",
          });
          return;
        }
        poserEtat(id, {
          phase: conf.envoi_simule ? "simule" : "envoye",
          erreur: null,
        });
      } catch (e) {
        // L'envoi ou la confirmation a cassé APRÈS la réclamation : la carte le
        // dit — et le job rendra l'en_envoi à la file (~10 min, reprise=true).
        poserEtat(id, {
          phase: null,
          erreur: `Un envoi a été tenté — vérifiez le fil avant de renvoyer. (${e?.message || e})`,
        });
      }
    },
    [charger],
  );

  // ── NON : deux étapes (motif → portée), et la règle « toujours ». ─────────
  const refuser = useCallback(async (prop, motif, portee, texte) => {
    try {
      const json = await postAction({
        action: "operateur_proposition_refuse",
        id: prop.id,
        motif,
        portee: portee || null,
        texte: texte || null,
      });
      poserEtat(prop.id, {
        phase: json.statut === "reportee" ? "reporte" : "refuse",
      });
      return true;
    } catch (e) {
      poserEtat(prop.id, { erreur: e?.message || "Le refus a échoué." });
      return false;
    }
  }, []);

  // ── LOT 2 — le coupe-circuit : basculer, et lire le bilan de l'arrêt. ─────
  const basculerEtat = useCallback(
    async (enMarche, motif) => {
      setArretErreur(null);
      try {
        await postAction({
          action: "operateur_etat_toggle",
          en_marche: enMarche,
          motif: motif || null,
        });
        setFeuilleArret(false);
        setArretBilan(null);
        charger();
      } catch (e) {
        setArretErreur(e?.message || "Le changement d'état a échoué.");
        setFeuilleArret(false);
      }
    },
    [charger],
  );

  const props_ = (source.data && source.data.propositions) || [];
  const plafond = source.data && source.data.plafond;
  const actif = !source.data || source.data.actif !== false;
  // L'état du coupe-circuit — ABSORBÉ dans la réponse de la liste (leçon M5).
  // Absent (réponse lot 1 / table absente) → on ne crie pas « arrêté ».
  const etatOp = (source.data && source.data.etat_operateur) || null;
  const arrete = !!(etatOp && etatOp.en_marche === false);

  // Le bilan de l'arrêt (« pendant l'arrêt : N messages arrivés ») — dérivé,
  // lu quand l'écran découvre l'arrêt.
  useEffect(() => {
    if (!arrete) return undefined;
    let vivant = true;
    (async () => {
      try {
        const json = await postAction({ action: "operateur_arret_bilan" });
        if (vivant && json.ok) setArretBilan(json);
      } catch (e) {
        console.error("[operateur] arret-bilan", e?.message || e);
      }
    })();
    return () => {
      vivant = false;
    };
  }, [arrete]);

  return (
    <div
      data-testid="operateur-view"
      style={{ maxWidth: 560, margin: "0 auto" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          margin: "4px 2px 2px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy-d)" }}>
          L'opérateur
        </h2>
        {source.ok === true && actif && (
          // Verdict Dimitri 2026-09-06 (arrêt 2) : la formule du proto quand il
          // y a un tri à annoncer — « N décisions — le plus pressé d'abord » ;
          // à 1, « le tri n'a pas de sens » → simplement « 1 décision ».
          <span
            data-testid="operateur-compteur"
            style={{ fontSize: 12, color: "var(--gris)" }}
          >
            {props_.length === 0
              ? "rien à décider"
              : props_.length === 1
                ? "1 décision"
                : `${props_.length} décisions — le plus pressé d'abord`}
          </span>
        )}
        {/* LOT 2 — le coupe-circuit, jamais un bouton mort : il bascule
            operateur_etat (DB). Le texte du proto au mot près. */}
        {source.ok === true &&
          actif &&
          etatOp &&
          (arrete ? (
            <button
              type="button"
              data-testid="operateur-relancer"
              className="fbz-press"
              onClick={() => basculerEtat(true, null)}
              style={{
                marginLeft: "auto",
                minHeight: 30,
                padding: "0 13px",
                borderRadius: 15,
                background: "var(--terra)",
                border: "none",
                color: "#fff",
                fontSize: 11.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Le relancer
            </button>
          ) : (
            <button
              type="button"
              data-testid="operateur-arreter"
              className="fbz-press"
              onClick={() => setFeuilleArret(true)}
              style={{
                marginLeft: "auto",
                minHeight: 30,
                padding: "0 13px",
                borderRadius: 15,
                background: "transparent",
                border: "1px solid var(--bord)",
                color: "var(--gris-d)",
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Tout arrêter
            </button>
          ))}
      </div>
      <p style={{ margin: "0 2px 14px", fontSize: 11, color: "var(--gris)" }}>
        Ce qui attend votre accord. Rien ne part sans vous.
      </p>

      {/* Ce qui est RÉELLEMENT allumé. Rendu SEULEMENT si le serveur l'envoie —
          il ne devine aucun drapeau (cf. LigneOrganes). */}
      <LigneOrganes organes={etatOp && etatOp.organes} />

      {/* LA VEILLE — hors de l'interrupteur de l'opérateur, délibérément : ce
          bloc ne montre pas ce que l'opérateur propose, il montre ce qui est
          VRAI dans la base sur de l'argent de voyageur. Le cacher derrière un
          interrupteur éteint rendrait invisibles des anomalies existantes. */}
      <VeilleCautions />
      <VeilleManques onAller={onAller} />

      {/* LOT 2 — les bascules : décisions (lot 1) / ce qu'il a fait / retenu. */}
      {source.ok === true && actif && (
        <div className="bascule" style={{ margin: "0 0 14px" }}>
          <button
            type="button"
            className={onglet === "decisions" ? "on" : ""}
            data-testid="onglet-decisions"
            onClick={() => setOnglet("decisions")}
          >
            Ses décisions
            <span style={{ fontSize: 9.5, opacity: 0.6, marginLeft: 5 }}>
              {props_.length}
            </span>
          </button>
          <button
            type="button"
            className={onglet === "fait" ? "on" : ""}
            data-testid="onglet-fait"
            onClick={() => setOnglet("fait")}
          >
            Ce qu'il a fait
          </button>
          <button
            type="button"
            className={onglet === "memoire" ? "on" : ""}
            data-testid="onglet-memoire"
            onClick={() => setOnglet("memoire")}
          >
            Retenu de vous
            {source.data &&
              source.data.attend &&
              source.data.attend.memoire > 0 && (
                <span style={{ fontSize: 9.5, opacity: 0.6, marginLeft: 5 }}>
                  {source.data.attend.memoire}
                </span>
              )}
          </button>
          {/* PIVOT — les réglages de l'IA vivent DANS la destination : l'onglet
              IA de ParamsPanel a disparu (il ne portait plus que de la prose,
              ses interrupteurs ayant été retirés au lot 4). */}
          <button type="button" className={onglet === 'reglages' ? 'on' : ''}
            data-testid="onglet-reglages" onClick={() => setOnglet('reglages')}>
            Réglages
          </button>
        </div>
      )}

      {arretErreur && (
        <div
          data-testid="operateur-arret-erreur"
          style={{
            margin: "0 0 10px",
            padding: "9px 12px",
            background: "var(--terra-bg)",
            border: "1px solid var(--terra-bd)",
            borderRadius: 12,
            fontSize: 11,
            color: "var(--terra-tx)",
          }}
        >
          {arretErreur}
        </div>
      )}

      {/* LOT 2 — l'état arrêté : dit, avec ce qu'il a manqué (dérivé M4). */}
      {arrete && (
        <div
          data-testid="operateur-arrete"
          className="carte"
          style={{
            borderColor: "var(--terra-bd)",
            background: "var(--terra-bg)",
            marginBottom: 14,
          }}
        >
          <b
            style={{
              display: "block",
              fontSize: 12.5,
              color: "var(--terra-tx)",
            }}
          >
            Il est à l'arrêt
          </b>
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: "var(--terra-tx)",
              marginTop: 3,
            }}
          >
            rien ne tourne — ni ses habitudes, ni ses envois
          </span>
          {arretBilan && (
            <span
              data-testid="operateur-arret-bilan"
              style={{
                display: "block",
                fontSize: 10.5,
                color: "var(--terra-tx)",
                marginTop: 6,
              }}
            >
              pendant l'arrêt : {arretBilan.messages_arrives} message
              {arretBilan.messages_arrives > 1 ? "s" : ""} arrivé
              {arretBilan.messages_arrives > 1 ? "s" : ""}
              {arretBilan.passages_sautes > 0
                ? ` · ${arretBilan.passages_sautes} passage${arretBilan.passages_sautes > 1 ? "s" : ""} sauté${arretBilan.passages_sautes > 1 ? "s" : ""}`
                : ""}
            </span>
          )}
        </div>
      )}

      {/* LOT 2 (leçon M5) — la matrice des 4 états : en marche en base MAIS
          env éteinte ⇒ l'écran dit l'état EFFECTIF, jamais « en marche ». */}
      {source.ok === true &&
        actif &&
        etatOp &&
        !arrete &&
        etatOp.effectif === false && (
          <p
            className="note"
            data-testid="operateur-env-eteinte"
            style={{ marginBottom: 14 }}
          >
            Il est en marche ici, mais sa génération est éteinte côté serveur
            (OPERATEUR_PROPOSE_ENABLED) — rien ne se prépare tant qu'elle ne
            revient pas.
          </p>
        )}

      {/* Q5 — quand il s'est arrêté, IL LE DIT (même count que le job). */}
      {onglet === "decisions" && plafond && plafond.atteint && (
        <div
          data-testid="operateur-plafond"
          className="carte"
          style={{
            borderColor: "var(--ambre-bd)",
            background: "var(--ambre-bg)",
          }}
        >
          <span
            style={{ fontSize: 12, color: "var(--ambre-tx)", fontWeight: 600 }}
          >
            {plafond.message}
          </span>
          {/* Verdict 2026-09-06 (arrêt 7) : la sous-ligne SEULE — le
              « il reprendra demain » n'est pas redit, il vit dans le titre. */}
          <span
            style={{
              display: "block",
              fontSize: 10.5,
              color: "var(--ambre-tx)",
              marginTop: 3,
            }}
          >
            Les messages restent dans l'Inbox.
          </span>
        </div>
      )}

      {/* Les trois vides, chacun sa phrase — jamais un vide muet. */}
      {source.ok === null && (
        <p className="note" data-testid="operateur-lecture">
          On lit les propositions…
        </p>
      )}
      {source.ok === false && (
        <p className="note" data-testid="operateur-echec">
          {source.motif}
        </p>
      )}
      {source.ok === true && !actif && (
        <p className="note" data-testid="operateur-inerte">
          L'opérateur n'est pas encore en service : sa mise en base n'est pas
          appliquée.
        </p>
      )}
      {onglet === "decisions" &&
        source.ok === true &&
        actif &&
        props_.length === 0 && (
          // Verdict Dimitri 2026-09-06 (revue arrêt 1) : la BONNE NOUVELLE
          // d'abord, au mot près du proto.
          <p className="note" data-testid="operateur-vide">
            <b>Tout est en ordre</b> — rien n'attend votre accord.
          </p>
        )}

      {/* LOT 2 — les deux autres bascules remplacent la file. */}
      {onglet === 'fait' && source.ok === true && actif && <OperateurFait nomLogement={nomLogement} />}
      {onglet === 'memoire' && source.ok === true && actif && <OperateurMemoire nomLogement={nomLogement} />}
      {/* Les réglages restent lisibles même à l'arrêt : ce sont des faits sur
          la machine, pas des gestes — les masquer priverait Dimitri de
          l'information au moment où il en a le plus besoin. */}
      {/* Les vagues ouvertes viennent du serveur (etat_operateur.vagues), en
          lecture seule — la carte ne devine rien si le bloc manque. */}
      {onglet === 'reglages' && <OperateurReglages vagues={etatOp && etatOp.vagues} />}

      {/* ── LES GESTES ─────────────────────────────────────────────────────
          Ils vivent dans la MÊME file que les brouillons — une seule file de
          décision (ADR-303 §4) — et se rendent seulement avec une autre carte.
          Ils sont affichés EN TÊTE : un geste est plus court à lire qu'un
          brouillon, et le faire passer après obligerait à parcourir des textes
          avant d'atteindre une décision d'un clic. */}
      {/* ── TROIS ÉTATS, PAS DEUX ────────────────────────────────────────
          1 · LEVIER RETIRÉ — l'action n'existe plus : il ne reste rien à lire
              dans la carte, une ligne suffit.
          2 · MARQUÉ FERMÉ  — l'action existe, elle n'est pas encore ouverte.
              La carte reste ENTIÈRE (le fait, le titre, le détail : Dimitri
              décidera plus tard, il doit pouvoir relire) et `CarteGeste` la
              rend inerte ET motivée. On ne rejuge PAS ici : deux lectures du
              marqueur divergeraient au premier correctif appliqué d'un côté.
          3 · OUVERT        — la même carte, cliquable.
          ⚠️ LE 2 A LONGTEMPS ÉTÉ CONFONDU AVEC LE 3 : le serveur marquait, et
          personne ne lisait — bouton navy « Le faire » sur un geste fermé. */}
      {onglet === "decisions" &&
        props_
          .filter((p) => p.nature === "geste")
          .map((p) =>
            p.levier_retire ? (
              // Levier retiré de l'allow-list depuis que la carte est née. On ne la
              // MASQUE pas — ce serait faire disparaître une décision en attente
              // sans rien dire — mais elle devient inerte et le dit.
              <p
                className="note"
                key={p.id}
                data-testid={`geste-retire-${p.id}`}
              >
                Un geste proposé n'est plus disponible (l'action a été retirée).
                Rien ne sera fait.
              </p>
            ) : (
              <CarteGeste
                key={p.id}
                proposition={p}
                etat={etats[p.id] || {}}
                onExecuter={lancerGeste}
              />
            ),
          )}

      {onglet === "decisions" &&
        props_
          .filter((p) => p.nature !== "geste")
          .map((p) => {
            const etat = etats[p.id] || {};
            const decide = ["envoye", "simule", "refuse", "reporte"].includes(
              etat.phase,
            );
            const sender = (p.meta && p.meta.sender_name) || "Voyageur";
            return (
              <div
                className="dec"
                key={p.id}
                data-testid={`operateur-carte-${p.id}`}
                style={{ marginBottom: 14 }}
              >
                {p.reprise && (
                  <div className="hd" data-testid={`carte-${p.id}-reprise`}>
                    <span className="q" style={{ color: "var(--ambre-tx)" }}>
                      Un envoi a été tenté — vérifiez le fil avant de renvoyer.
                    </span>
                  </div>
                )}
                <div className="rail">
                  {/* 1 · ce qu'il a vu */}
                  <div className="step">
                    <span className="dot vu">👁</span>
                    <div className="bd">
                      <span className="kl">CE QU'IL A VU</span>
                      <div className="voy">
                        <div className="voyT">
                          <span className="voyAv">{initialesDe(sender)}</span>
                          <span className="voyN">{sender}</span>
                          <span className="voyM">
                            {nomLogement(p.logement_id)} ·{" "}
                            {quandCourt(
                              (p.meta && p.meta.source_created_at) ||
                                p.created_at,
                            )}
                          </span>
                        </div>
                        <p className="voyX">{p.vu}</p>
                      </div>
                    </div>
                  </div>
                  {/* 2 · ce qu'il en pense */}
                  <div className="step">
                    <span className="dot lui">{microIc(12)}</span>
                    <div className="bd">
                      <span className="kl">CE QU'IL EN PENSE</span>
                      <p className="pense">{p.dit}</p>
                    </div>
                  </div>
                  {/* 3 · sa réponse, prête */}
                  <div className="step">
                    <span className="dot act">✎</span>
                    <div className="bd">
                      <span className="kl">SA RÉPONSE, PRÊTE</span>
                      <div className="fait">
                        {etat.retouche ? (
                          <textarea
                            data-testid={`carte-${p.id}-retouche`}
                            value={etat.texte}
                            onChange={(e) =>
                              poserEtat(p.id, { texte: e.target.value })
                            }
                            style={{
                              width: "100%",
                              minHeight: 110,
                              border: "none",
                              outline: "none",
                              background: "none",
                              font: "inherit",
                              fontSize: 12.5,
                              lineHeight: 1.6,
                              color: "var(--navy-d)",
                              resize: "vertical",
                            }}
                          />
                        ) : (
                          <p className="faitX">{p.draft_texte}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {etat.erreur && (
                  <div
                    data-testid={`carte-${p.id}-erreur`}
                    style={{
                      margin: "0 14px 10px",
                      padding: "9px 12px",
                      background: "var(--terra-bg)",
                      border: "1px solid var(--terra-bd)",
                      borderRadius: 12,
                      fontSize: 11,
                      color: "var(--terra-tx)",
                      lineHeight: 1.5,
                    }}
                  >
                    {etat.erreur}
                  </div>
                )}
                {microNote === p.id && (
                  <div
                    data-testid={`carte-${p.id}-micro-note`}
                    style={{
                      margin: "0 14px 10px",
                      padding: "9px 12px",
                      background: "var(--ciel-p)",
                      border: "1px solid #c3d5e4",
                      borderRadius: 12,
                      fontSize: 11,
                      color: "var(--navy-d)",
                    }}
                  >
                    Pas encore — lui parler arrivera avec l'opérateur complet.
                  </div>
                )}

                {decide ? (
                  <div className="go">
                    <span
                      className="tag t-v"
                      data-testid={`carte-${p.id}-decidee`}
                      style={{ padding: "11px 16px" }}
                    >
                      {etat.phase === "envoye"
                        ? "Envoyé"
                        : etat.phase === "simule"
                          ? "simulé — le maître d'envoi est fermé"
                          : etat.phase === "reporte"
                            ? "Plus tard — il vous la représentera"
                            : "Refusé — à vous"}
                    </span>
                  </div>
                ) : etat.retouche ? (
                  <div className="go">
                    {/* Une retouche VIDE n'est pas envoyable — bouton désarmé. */}
                    <button
                      type="button"
                      className="bt bt-n"
                      disabled={
                        etat.phase === "envoi" ||
                        arrete ||
                        !(etat.texte || "").trim()
                      }
                      data-testid={`carte-${p.id}-envoyer-retouche`}
                      onClick={() =>
                        envoyer(p, (etat.texte || "").trim(), true)
                      }
                    >
                      {etat.phase === "envoi"
                        ? "Envoi…"
                        : "Envoyer la retouche"}
                    </button>
                    <button
                      type="button"
                      className="bt bt-s"
                      onClick={() =>
                        poserEtat(p.id, { retouche: false, texte: null })
                      }
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  <div className="go">
                    {/* LOT 2 — à l'arrêt, les gestes sont DÉSARMÉS (lecture seule
                    sauf « Le relancer ») : le serveur refuserait de toute
                    façon (409 a_l_arret). */}
                    <button
                      type="button"
                      className="bt bt-n"
                      disabled={etat.phase === "envoi" || arrete}
                      data-testid={`carte-${p.id}-envoyer`}
                      onClick={() => envoyer(p, p.draft_texte, false)}
                    >
                      {etat.phase === "envoi" ? "Envoi…" : "Envoyer"}
                    </button>
                    <button
                      type="button"
                      className="bt bt-s"
                      data-testid={`carte-${p.id}-retoucher`}
                      disabled={arrete}
                      onClick={() =>
                        poserEtat(p.id, {
                          retouche: true,
                          texte: p.draft_texte,
                        })
                      }
                    >
                      Retoucher
                    </button>
                    <button
                      type="button"
                      className="bt bt-s"
                      data-testid={`carte-${p.id}-non`}
                      disabled={arrete}
                      onClick={() =>
                        setRefus({
                          prop: p,
                          etape: "motif",
                          motif: null,
                          texte: "",
                        })
                      }
                    >
                      Non
                    </button>
                    {/* D4 — le micro est un bouton HONNÊTE : « pas encore ». */}
                    <button
                      type="button"
                      className="bt bt-s"
                      data-testid={`carte-${p.id}-micro`}
                      title="pas encore"
                      aria-label="Micro — pas encore"
                      disabled={arrete}
                      style={{ flex: "0 0 auto", padding: "0 13px" }}
                      onClick={() =>
                        setMicroNote(microNote === p.id ? null : p.id)
                      }
                    >
                      {microIc(15)}
                    </button>
                  </div>
                )}

                {/* V10 (ADR-304 point 5) — les cartes de réponse voyageur GARDENT
                « il proposera toujours » : le segment demande/propose/fait seul
                n'existe QUE sur les signalements d'habitude (OperateurFait). */}
                {!decide && (
                  <div className="apr">
                    <span className="lb">La prochaine fois</span>
                    <span
                      data-testid={`carte-${p.id}-toujours`}
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: "var(--navy-d)",
                      }}
                    >
                      il proposera toujours
                    </span>
                  </div>
                )}
              </div>
            );
          })}

      {/* ── LA FEUILLE DU NON — les deux étapes du proto. ─────────────────── */}
      {refus && refus.etape === "motif" && (
        <Feuille titre="Pourquoi non ?" onFermer={() => setRefus(null)}>
          <p className="note" style={{ margin: "0 0 12px" }}>
            Dites-lui ce qui ne va pas — c'est comme ça qu'il arrête de vous
            reproposer la même chose.
          </p>
          <ChoixLigne
            testId="refus-moi"
            titre="Je m'en occupe moi-même"
            sous="il vous rend la main et note que ce cas vous revient"
            onClic={async () => {
              const ok = await refuser(refus.prop, "moi", "exception", null);
              if (ok) setRefus(null);
            }}
          />
          <ChoixLigne
            testId="refus-faux"
            titre="Ce n'est pas la bonne réponse"
            sous="il vous demandera ce qu'il aurait fallu faire"
            onClic={() => setRefus({ ...refus, etape: "texte" })}
          />
          <ChoixLigne
            testId="refus-tard"
            titre="Pas maintenant"
            sous="il vous la représente plus tard, rien n'est perdu"
            onClic={async () => {
              const ok = await refuser(refus.prop, "tard", null, null);
              if (ok) setRefus(null);
            }}
          />
          <div className="act">
            <button
              type="button"
              className="bt bt-s"
              onClick={() => setRefus(null)}
            >
              Revenir à la liste
            </button>
          </div>
        </Feuille>
      )}
      {refus && refus.etape === "texte" && (
        <Feuille
          titre="Qu'aurait-il fallu faire ?"
          onFermer={() => setRefus(null)}
        >
          <div className="carte" style={{ margin: 0 }}>
            <textarea
              data-testid="refus-texte"
              value={refus.texte}
              placeholder="Dites-lui la bonne réponse…"
              onChange={(e) => setRefus({ ...refus, texte: e.target.value })}
              style={{
                width: "100%",
                minHeight: 84,
                border: "none",
                outline: "none",
                background: "none",
                font: "inherit",
                fontSize: 12.5,
                lineHeight: 1.6,
                color: "var(--navy-d)",
                resize: "vertical",
              }}
            />
          </div>
          <div className="act">
            <button
              type="button"
              className="bt bt-p"
              data-testid="refus-continuer"
              onClick={() => setRefus({ ...refus, etape: "portee" })}
            >
              Continuer
            </button>
            <button
              type="button"
              className="bt bt-s"
              onClick={() => setRefus({ ...refus, etape: "motif" })}
            >
              Revenir
            </button>
          </div>
        </Feuille>
      )}
      {refus && refus.etape === "portee" && (
        <Feuille
          titre="Juste cette fois, ou toujours ?"
          onFermer={() => setRefus(null)}
        >
          <p className="note" style={{ margin: "0 0 12px" }}>
            Pour qu'il retienne au bon niveau, dites-lui si c'est une exception
            ou une règle.
          </p>
          <ChoixLigne
            testId="portee-exception"
            titre="Juste cette fois"
            sous="il ne change rien à ses habitudes"
            onClic={async () => {
              const ok = await refuser(
                refus.prop,
                "faux",
                "exception",
                refus.texte.trim() || null,
              );
              if (ok) setRefus(null);
            }}
          />
          {refus.texte.trim() ? (
            <>
              <ChoixLigne
                testId="portee-toujours"
                titre={`Toujours, pour ${nomLogement(refus.prop.logement_id)}`}
                sous="il en fait une règle sur ce logement"
                onClic={() =>
                  setRefus({
                    ...refus,
                    etape: "confirmer-regle",
                    portee: "logement",
                  })
                }
              />
              {/* LOT 2 (leçon B2) — le 3e choix du proto, au mot près. */}
              <ChoixLigne
                testId="portee-partout"
                titre="Toujours, partout"
                sous="il en fait une règle générale"
                onClic={() =>
                  setRefus({
                    ...refus,
                    etape: "confirmer-regle",
                    portee: "partout",
                  })
                }
              />
            </>
          ) : (
            <p className="note" style={{ margin: "2px 0 8px" }}>
              « Toujours » demande la bonne réponse écrite à l'étape d'avant —
              sans elle, il n'a rien à retenir.
            </p>
          )}
          <div className="act">
            <button
              type="button"
              className="bt bt-s"
              onClick={() => setRefus({ ...refus, etape: "texte" })}
            >
              Revenir
            </button>
          </div>
        </Feuille>
      )}
      {refus && refus.etape === "confirmer-regle" && (
        <Feuille titre="Il va la retenir" onFermer={() => setRefus(null)}>
          {/* Q2 — Dimitri VOIT le texte et confirme AVANT l'écriture. */}
          <p className="note" style={{ margin: "0 0 10px" }}>
            {refus.portee === "partout"
              ? "Il retient cette règle pour tous vos logements — rien ne se retient sans votre accord."
              : "Il retient cette règle pour ce logement — rien ne se retient sans votre accord."}
          </p>
          <div className="carte" style={{ margin: "0 0 10px" }}>
            <p
              data-testid="regle-texte"
              style={{
                margin: 0,
                fontSize: 12.5,
                lineHeight: 1.6,
                color: "var(--navy-d)",
              }}
            >
              {refus.texte.trim()}
            </p>
          </div>
          <div className="act">
            <button
              type="button"
              className="bt bt-p"
              data-testid="regle-confirmer"
              onClick={async () => {
                const portee =
                  refus.portee === "partout" ? "partout" : "logement";
                const ok = await refuser(
                  refus.prop,
                  "faux",
                  portee,
                  refus.texte.trim(),
                );
                if (!ok) {
                  setRefus(null);
                  return;
                }
                try {
                  const json = await postAction({
                    action: "operateur_regle_toujours",
                    texte: refus.texte.trim(),
                    portee,
                    proposition_id: refus.prop.id,
                    logement_id: refus.prop.logement_id || null,
                  });
                  // LOT 2 (leçon M6) — LA RÉPONSE DIT LA MAISON : l'écran
                  // adapte son texte, il ne devine jamais le flag serveur.
                  const noms = (json.logements || [])
                    .map(nomLogement)
                    .join(", ");
                  setRefus({
                    ...refus,
                    etape: "regle-ecrite",
                    portee,
                    maison: json.maison || "fiches",
                    ecrits: noms,
                  });
                } catch (e) {
                  setRefus(null);
                  poserEtat(refus.prop.id, {
                    erreur: `La règle n'a pas pu être écrite : ${e?.message || e}`,
                  });
                }
              }}
            >
              Écrire la règle
            </button>
            <button
              type="button"
              className="bt bt-s"
              onClick={() => setRefus({ ...refus, etape: "portee" })}
            >
              Revenir
            </button>
          </div>
        </Feuille>
      )}
      {refus && refus.etape === "regle-ecrite" && (
        <Feuille titre="C'est retenu" onFermer={() => setRefus(null)}>
          <div
            className="carte"
            style={{
              margin: 0,
              background: "var(--vert-bg)",
              borderColor: "var(--vert-bd)",
            }}
          >
            <p
              data-testid="regle-ecrite"
              style={{
                margin: 0,
                fontSize: 12,
                color: "var(--vert-tx)",
                lineHeight: 1.6,
              }}
            >
              {refus.maison === "memoire"
                ? refus.portee === "partout"
                  ? "✓ Retenu, partout."
                  : `✓ Retenu, pour ${nomLogement(refus.prop.logement_id)}.`
                : `✓ Règle écrite dans les fiches de ${refus.ecrits || "—"}.`}
            </p>
          </div>
          <div className="act">
            <button
              type="button"
              className="bt bt-p"
              onClick={() => setRefus(null)}
            >
              Revenir à la liste
            </button>
          </div>
        </Feuille>
      )}
      {/* ── LA FEUILLE « TOUT ARRÊTER ? » — le proto au mot près. ─────────── */}
      {feuilleArret && (
        <Feuille titre="Tout arrêter ?" onFermer={() => setFeuilleArret(false)}>
          <div
            className="dang"
            data-testid="arret-dang"
            style={{
              background: "var(--terra-bg)",
              border: "1px solid var(--terra-bd)",
              borderRadius: 12,
              padding: "11px 12px",
              fontSize: 11.5,
              color: "var(--terra-tx)",
              lineHeight: 1.55,
            }}
          >
            Il s'arrête net : plus aucune habitude, plus aucun envoi, plus
            aucune écriture. Ce qui est déjà parti reste parti.
          </div>
          <p className="note" style={{ margin: "10px 0 12px" }}>
            Vous pourrez le relancer quand vous voulez. Il vous dira ce qu'il a
            manqué pendant l'arrêt.
          </p>
          <div className="act">
            <button
              type="button"
              className="bt bt-p"
              data-testid="arret-confirmer"
              style={{ background: "var(--terra)" }}
              onClick={() => basculerEtat(false, null)}
            >
              Tout arrêter
            </button>
            <button
              type="button"
              className="bt bt-s"
              onClick={() => setFeuilleArret(false)}
            >
              Annuler
            </button>
          </div>
        </Feuille>
      )}
    </div>
  );
}
